// TermsPage — UAVSimNet Terms of Service / Master Services Agreement
// Issued by The RLK Group (Scottish limited company, SC781391).
// Governing law: Scotland. UK / Scottish defence-tech SaaS.

import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { buildBreadcrumbListSchema } from "@/lib/schema";

const EFFECTIVE_DATE = "25 April 2026";

export default function TermsPage() {
    return (
        <>
            <SeoHead
                path="/terms"
                schemaJsonLd={[
                    buildBreadcrumbListSchema({ path: "/terms", label: "Terms of Service" }),
                ]}
            />
        <div data-testid="terms-page">
            <section className="section" style={{ paddingTop: 64 }}>
                <div className="section__inner legal-doc">
                    <span className="section__eyebrow">// LEGAL · TERMS OF SERVICE</span>
                    <h1 className="section__heading">Terms of Service</h1>
                    <div
                        className="legal-doc__banner"
                        data-testid="terms-issued-banner"
                    >
                        Issued by The RLK Group · Company No. SC781391 · Effective from {EFFECTIVE_DATE}
                    </div>

                    <h2>1. About these terms</h2>
                    <p>
                        These Terms of Service (the "<strong>Terms</strong>") are issued
                        by <strong>The RLK Group</strong>, a private limited company
                        incorporated in Scotland under company number{" "}
                        <strong>SC781391</strong>, with its registered office at{" "}
                        Unit 4 Strathclyde Business Centre, Cambuslang, United Kingdom,
                        G72 7XR ("<strong>RLK</strong>", "<strong>we</strong>", "
                        <strong>us</strong>"). They govern access to and use of{" "}
                        <strong>UAVSimNet</strong>, our counter-UAS mission analysis
                        platform (the "<strong>Platform</strong>"), and any related
                        services we provide (together, the "<strong>Services</strong>").
                    </p>
                    <p>
                        These Terms apply to any party that submits an access request,
                        accepts an invite link, authenticates to the Platform, or
                        otherwise uses the Services (the "<strong>Customer</strong>",
                        "<strong>you</strong>"). Acceptance is recorded at access-request
                        time through the four consent checkboxes on our public
                        access-request form, with a timestamp stored against the
                        submission. Using the Services after the Effective Date shown
                        above constitutes continuing acceptance of the Terms in force.
                    </p>

                    <h2>2. Definitions</h2>
                    <ul>
                        <li>
                            <strong>Customer</strong> — the organisation on whose behalf
                            an access request was approved and a tenant provisioned.
                        </li>
                        <li>
                            <strong>Authorised User</strong> — an individual employee,
                            contractor, or agent of the Customer who holds credentials
                            issued by RLK to access the Platform on Customer's behalf.
                        </li>
                        <li>
                            <strong>Platform</strong> — UAVSimNet, including its web
                            application, public site, operator console, simulation
                            engine, documentation, and any supporting infrastructure.
                        </li>
                        <li>
                            <strong>Services</strong> — access to the Platform and any
                            support, consultancy, or professional services RLK agrees
                            in writing to provide.
                        </li>
                        <li>
                            <strong>Customer Data</strong> — scenarios, parameter sets,
                            threat profiles, run inputs, run outputs, and any other
                            content the Customer or its Authorised Users generate,
                            upload, or produce using the Platform.
                        </li>
                        <li>
                            <strong>Confidential Information</strong> — information
                            disclosed by one party to the other that is marked as
                            confidential or that, by its nature, a reasonable person
                            would treat as confidential.
                        </li>
                        <li>
                            <strong>Subscription Term</strong> — the period for which
                            Customer holds a paid entitlement to the Services, as
                            recorded in the applicable order form.
                        </li>
                        <li>
                            <strong>Effective Date</strong> — the date shown in the
                            banner at the top of this document, or the date Customer
                            first accepts these Terms, whichever is later.
                        </li>
                    </ul>

                    <h2>3. Access and accounts</h2>
                    <p>
                        Access to the Platform is enterprise-only. RLK provisions
                        tenants manually after reviewing each access request against
                        our eligibility, compliance, and export-control policies. We
                        may decline any access request at our sole discretion. There
                        is one tenant per Customer; if additional tenants are required
                        (for example, for separate programmes or deployment regions),
                        they are agreed in writing and priced separately.
                    </p>
                    <p>
                        Authorised Users receive access via single-use invite tokens
                        that expire once consumed or after a defined window. Each
                        Authorised User must set their own password at invite
                        consumption and must not share credentials with any other
                        individual. Customer is responsible for keeping passwords
                        confidential, for promptly revoking access when a user leaves,
                        and for all activity conducted under credentials issued to it.
                        We may suspend or revoke access where we reasonably believe
                        credentials have been compromised, shared, or misused.
                    </p>

                    <h2>4. Subscription, term, and renewal</h2>
                    <p>
                        The Services are provided on an annual enterprise-subscription
                        basis. Commercial terms — including price, Subscription Term,
                        renewal dates, cancellation notice, and any usage limits — are
                        agreed in writing during onboarding and recorded in an order
                        form or equivalent commercial document signed by both parties.
                        We do not list pricing publicly.
                    </p>
                    <p>
                        On expiry of the Subscription Term, access to the Platform is
                        automatically suspended unless the subscription has been
                        renewed by mutual written agreement. Renewal is not automatic.
                        Customer remains responsible for exporting any Customer Data
                        it wishes to retain before expiry; post-expiry retrieval is
                        subject to §13 (Term and termination). Prepaid fees are not
                        refundable if Customer terminates for convenience before the
                        end of a Subscription Term.
                    </p>

                    <h2>5. Acceptable use</h2>
                    <p>
                        Customer's use of the Services is subject to our Acceptable
                        Use Policy (the "<strong>AUP</strong>"), available at{" "}
                        <Link to="/aup">/aup</Link>, which is incorporated into these
                        Terms by reference. Breach of the AUP is a material breach of
                        these Terms. RLK may suspend access to the Services
                        immediately, without prior notice, where we reasonably believe
                        continued access would cause serious harm to the Platform,
                        to other customers, or to the public interest.
                    </p>

                    <h2>6. Customer Data</h2>
                    <p>
                        As between the parties, Customer owns all right, title, and
                        interest in Customer Data. RLK claims no ownership over
                        scenarios, parameter sets, threat profiles, or run results
                        produced by Customer. Customer grants RLK a limited,
                        non-exclusive, worldwide, royalty-free licence to host, store,
                        process, transmit, and display Customer Data strictly as
                        necessary to provide the Services, to respond to Customer's
                        support requests, and — only where Customer has given separate
                        consent through the access-request form — to use anonymised
                        artefacts for the purpose described in §7.
                    </p>
                    <p>
                        Our handling of personal data within Customer Data is
                        described in our Privacy Policy at{" "}
                        <Link to="/privacy">/privacy</Link>. Customer warrants that it
                        has the necessary rights, authorisations, and lawful bases to
                        upload Customer Data to the Platform and to permit RLK to
                        process it as described in these Terms.
                    </p>

                    <h2>7. Training data participation</h2>
                    <p>
                        Where Customer has opted in to the training-data mirror at
                        access-request time, RLK may duplicate a defined set of
                        anonymised artefacts from each successful run into an internal
                        training mirror used to improve future releases of the
                        Platform. Only a hashed tenant identifier is written alongside
                        the artefact files; direct personal identifiers are stripped
                        before the data enters the mirror. Customer may withdraw from
                        the training mirror at any time by giving written notice to
                        RLK at the registered office address in §20; withdrawal
                        applies to future runs only and does not retroactively delete
                        mirror entries already created, although Customer may request
                        such deletion as described in the Privacy Policy.
                    </p>

                    <h2>8. Confidentiality</h2>
                    <p>
                        Each party agrees (a) to use the other's Confidential
                        Information only as reasonably necessary to perform these
                        Terms, (b) to protect it with at least the degree of care it
                        applies to its own confidential information of similar
                        importance, and (c) not to disclose it to any third party
                        without the discloser's prior written consent, except to
                        employees, contractors, advisers, or sub-processors bound by
                        confidentiality obligations at least as protective as those
                        in these Terms. The obligations in this clause do not apply
                        to information that is or becomes publicly known without
                        fault of the receiver, was known to the receiver without
                        restriction before disclosure, is independently developed
                        without reference to the Confidential Information, or is
                        required to be disclosed by law or court order (in which
                        case the receiver shall, where lawful, give the discloser
                        reasonable notice and cooperate in seeking confidential
                        treatment). This clause survives termination of these Terms
                        for a period of five (5) years.
                    </p>

                    <h2>9. Intellectual property</h2>
                    <p>
                        RLK retains all right, title, and interest in and to the
                        Platform, including all software, models, simulation engines,
                        pre-loaded demonstrations, documentation, visual design, and
                        any improvements, modifications, or derivative works thereof.
                        No rights or licences are granted to Customer other than as
                        expressly set out in these Terms. Customer shall not (and
                        shall not permit any Authorised User or third party to)
                        reverse engineer, decompile, disassemble, or otherwise attempt
                        to derive the source code, model weights, training data, or
                        non-public interfaces of the Platform, nor create derivative
                        works of the Platform, except as expressly permitted by
                        mandatory law.
                    </p>
                    <p>
                        If Customer provides feedback, suggestions, or improvement
                        ideas in connection with the Services, RLK may use and
                        incorporate that feedback into the Platform without
                        obligation, payment, or attribution.
                    </p>

                    <h2>10. Compliance and export control</h2>
                    <p>
                        Counter-UAS analytic software is dual-use technology and may
                        be subject to export controls under UK Strategic Export
                        Controls (administered through SPIRE), the EU dual-use
                        regulation, the United States International Traffic in Arms
                        Regulations (ITAR) and Export Administration Regulations
                        (EAR), and equivalent regimes in Customer's jurisdiction.
                    </p>
                    <p>
                        <strong>Customer is solely responsible for compliance with
                        all export-control and sanctions laws applicable to its use
                        of the Services.</strong> Customer warrants at access-request
                        time, and re-confirms on each login, that: (a) it is not
                        located in, organised under the laws of, or resident in a
                        country subject to comprehensive sanctions; (b) it is not on,
                        and is not owned or controlled by any party on, a prohibited
                        persons list maintained by the United Kingdom, the European
                        Union, the United Nations, the United States, or any other
                        applicable authority; and (c) it will not make the Services
                        or any output of the Services available to any such party.
                        RLK reserves the right to suspend or terminate access
                        immediately, without notice, where it reasonably believes
                        continued provision of the Services would breach these
                        controls.
                    </p>

                    <h2>11. Warranties and disclaimers</h2>
                    <p>
                        Each party warrants that it has full corporate authority to
                        enter into and perform these Terms. <strong>Save as expressly
                        set out in these Terms, and to the fullest extent permitted
                        by law, RLK provides the Services "as is" and "as available",
                        without warranty or representation of any kind</strong>,
                        whether express, implied, statutory, or otherwise, including
                        any implied warranty of merchantability, satisfactory
                        quality, fitness for a particular purpose, accuracy, or
                        non-infringement.
                    </p>
                    <p>
                        UAVSimNet is a simulation and analysis platform. RLK does not
                        warrant that simulation results will accurately predict the
                        outcome of any real-world engagement, and simulation outputs
                        are intended to inform engineering and operational judgement,
                        not to replace it. All defence, procurement, and operational
                        decisions remain solely Customer's responsibility. RLK does
                        not guarantee fitness of the Services for any specific
                        operational deployment, mission, or real-time fire-control
                        use.
                    </p>

                    <h2>12. Limitation of liability</h2>
                    <p>
                        To the fullest extent permitted by Scottish law, RLK's
                        aggregate liability to Customer arising out of or in
                        connection with these Terms, whether in contract, delict
                        (tort), negligence, breach of statutory duty, or otherwise,
                        is capped at the total fees paid by Customer to RLK for the
                        Services in the twelve (12) months immediately preceding the
                        event giving rise to the claim.
                    </p>
                    <p>
                        In no event shall RLK be liable for any indirect,
                        consequential, special, exemplary, or punitive damages, nor
                        for any loss of profits, revenue, business, anticipated
                        savings, goodwill, or data, arising out of or in connection
                        with these Terms, even if RLK has been advised of the
                        possibility of such damages. Nothing in these Terms excludes
                        or limits liability for fraud or fraudulent misrepresentation,
                        for death or personal injury caused by a party's negligence,
                        or for any other liability that cannot lawfully be excluded
                        or limited under Scottish law.
                    </p>

                    <h2>13. Term and termination</h2>
                    <p>
                        These Terms take effect on the Effective Date and continue
                        for the Subscription Term, unless terminated earlier under
                        this clause. Either party may terminate these Terms with
                        immediate effect on written notice if the other party
                        commits a material breach that is incapable of remedy, or
                        that it fails to remedy within thirty (30) days of receiving
                        written notice requiring it to do so, or becomes insolvent,
                        enters administration, ceases trading, or suffers an
                        analogous event in its jurisdiction.
                    </p>
                    <p>
                        RLK may terminate these Terms with immediate effect where
                        Customer materially breaches the AUP, fails to comply with
                        §10 (Compliance and export control), fails to pay undisputed
                        fees, or where termination is required by law.
                    </p>
                    <p>
                        On termination, all rights of access cease. Customer may
                        request an export of its Customer Data within thirty (30)
                        days of the effective date of termination; after that window,
                        Customer Data is deleted, except that (a) anonymised
                        training-mirror entries are retained unless separately
                        deleted under the Privacy Policy, and (b) audit logs and
                        commercial records are retained for the periods set out in
                        the Privacy Policy and as required by law. Clauses intended
                        by their nature to survive termination — including §6, §8,
                        §9, §10, §11, §12, §13, §18, and §19 — survive.
                    </p>

                    <h2>14. Suspension</h2>
                    <p>
                        RLK may suspend Customer's access to the Services, in whole
                        or in part, where: (a) Customer has failed to pay undisputed
                        fees when due; (b) RLK reasonably believes Customer has
                        breached the AUP or §10; (c) a security incident requires
                        containment; or (d) RLK is required to do so by law or
                        regulatory compulsion. We will give Customer reasonable
                        notice of suspension where it is practicable and lawful to
                        do so; in urgent circumstances, we may suspend immediately
                        and notify Customer as soon as reasonably practicable.
                    </p>

                    <h2>15. Force majeure</h2>
                    <p>
                        Neither party is liable for any failure or delay in
                        performing its obligations under these Terms (other than
                        payment) to the extent caused by events beyond its
                        reasonable control, including acts of God, war, terrorism,
                        civil disturbance, pandemic, strike, cyber-attack,
                        infrastructure failure, or action of a competent authority.
                        The affected party shall notify the other promptly and use
                        reasonable endeavours to mitigate the effect.
                    </p>

                    <h2>16. Notices</h2>
                    <p>
                        Written notices to The RLK Group shall be sent in writing
                        to its registered office at Unit 4 Strathclyde Business
                        Centre, Cambuslang, United Kingdom, G72 7XR, or to such
                        other address as The RLK Group may notify in writing from
                        time to time. Such notices are deemed received on the second
                        business day after posting by first-class recorded delivery.
                        Written notices to Customer shall be sent to the email
                        address on the Customer's account record and are deemed
                        received on the next business day after transmission, unless
                        a delivery-failure notification is received.
                    </p>

                    <h2>17. Assignment</h2>
                    <p>
                        Customer may not assign, novate, sub-licence, or otherwise
                        transfer these Terms or any rights or obligations under them
                        without RLK's prior written consent. RLK may assign or
                        novate these Terms to a successor in business or to a
                        corporate affiliate without Customer's consent, provided the
                        successor assumes the obligations on materially equivalent
                        terms.
                    </p>

                    <h2>18. Governing law and jurisdiction</h2>
                    <p>
                        These Terms and any non-contractual obligations arising out
                        of or in connection with them are governed by{" "}
                        <strong>Scottish law</strong>. The parties submit to the
                        exclusive jurisdiction of the{" "}
                        <strong>Court of Session, Edinburgh, Scotland</strong>, for
                        any dispute arising out of or in connection with these
                        Terms, save that RLK may seek injunctive or interdict relief
                        in any court of competent jurisdiction to protect its
                        Confidential Information or intellectual property.
                    </p>

                    <h2>19. Entire agreement</h2>
                    <p>
                        These Terms, together with the AUP, the Privacy Policy, and
                        any executed order form or statement of work, constitute the
                        entire agreement between the parties in relation to the
                        Services, and supersede all prior or contemporaneous
                        communications, proposals, and understandings. No
                        modification to these Terms is effective unless made in
                        writing and signed by an authorised representative of each
                        party. If any provision is held unenforceable by a court of
                        competent jurisdiction, the remaining provisions remain in
                        full force and effect, and the unenforceable provision shall
                        be modified to the minimum extent necessary to make it
                        enforceable while preserving its original intent.
                    </p>

                    <h2>20. Contact</h2>
                    <p>
                        Legal notices, termination notices, and all other formal
                        correspondence under these Terms shall be sent in writing to
                        The RLK Group at its registered office, or to such other
                        address as The RLK Group may notify in writing from time to
                        time:
                    </p>
                    <p>
                        <strong>The RLK Group</strong> (Company No. SC781391)
                        <br />
                        Unit 4 Strathclyde Business Centre
                        <br />
                        Cambuslang
                        <br />
                        United Kingdom
                        <br />
                        G72 7XR
                    </p>

                    <div className="legal-doc__crosslinks" data-testid="terms-crosslinks">
                        <span>See also:</span>
                        <Link to="/privacy">Privacy Policy</Link>
                        <span>·</span>
                        <Link to="/aup">Acceptable Use Policy</Link>
                    </div>
                    <div className="legal-doc__updated">Last updated: {EFFECTIVE_DATE}</div>
                </div>
            </section>
        </div>
        </>
    );
}
